import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { RefreshCw, ShoppingCart, Truck, AlertTriangle, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const HEALTH_STYLES = {
  green: { dot: "bg-emerald-500", text: "text-emerald-300", label: "Operativo" },
  yellow: { dot: "bg-amber-500", text: "text-amber-300", label: "In ritardo" },
  red: { dot: "bg-red-500 animate-pulse", text: "text-red-300", label: "Attenzione" },
};

const formatRelative = (iso) => {
  if (!iso) return "mai";
  try {
    const ts = new Date(iso);
    const diffSec = Math.floor((Date.now() - ts.getTime()) / 1000);
    const abs = Math.abs(diffSec);
    const suffix = diffSec >= 0 ? "fa" : "";
    const prefix = diffSec < 0 ? "tra " : "";
    let value;
    if (abs < 60) value = `${abs}s`;
    else if (abs < 3600) value = `${Math.floor(abs / 60)} min`;
    else if (abs < 86400) value = `${Math.floor(abs / 3600)} ore`;
    else value = `${Math.floor(abs / 86400)} giorni`;
    return `${prefix}${value}${suffix ? " " + suffix : ""}`.trim();
  } catch {
    return "—";
  }
};

const getAuthHeaders = () => {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const HealthDot = ({ health }) => {
  const s = HEALTH_STYLES[health] || HEALTH_STYLES.red;
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} />
      <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
    </div>
  );
};

export const SyncStatusWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(null); // 'ebay' | 'packlink' | null
  const [msg, setMsg] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/sync-status`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      console.error("[sync-status] fetch failed:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 60_000); // auto-refresh every minute
    return () => clearInterval(id);
  }, [fetchStatus]);

  const triggerEbay = async () => {
    setTriggering("ebay");
    setMsg(null);
    try {
      const r = await axios.post(
        `${API}/ebay/sync-orders?lookback_hours=24`,
        {},
        { headers: getAuthHeaders(), withCredentials: true, timeout: 60_000 }
      );
      const d = r.data || {};
      const errs = Array.isArray(d.stock_errors) ? d.stock_errors : [];
      let text;
      if (d.ok === false) {
        text = `eBay: ${d.reason || "errore"}`;
      } else {
        text =
          `eBay → ${d.fetched || 0} ordini visti · ${d.new || 0} importati · ${d.skipped || 0} già fatti`;
        if (errs.length > 0) {
          // Surface SKU mismatches so admin sees exactly why stock didn't drop.
          // Truncate to first 3 to keep the toast compact.
          const preview = errs.slice(0, 3).map((e) => `• ${e}`).join("\n");
          text += `\n⚠️ ${errs.length} errori stock:\n${preview}${errs.length > 3 ? `\n…e altri ${errs.length - 3}` : ""}`;
        }
      }
      setMsg({ ok: d.ok !== false && errs.length === 0, text });
      await fetchStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(null), 12000);
    }
  };

  const triggerPacklink = async () => {
    setTriggering("packlink");
    setMsg(null);
    try {
      const r = await axios.post(
        `${API}/packlink/auto-link`,
        {},
        { headers: getAuthHeaders(), withCredentials: true, timeout: 60_000 }
      );
      const d = r.data || {};
      setMsg({
        ok: true,
        text: `Packlink → ${d.linked || 0} agganciati · ${d.already_linked || 0} già linkati · ${d.no_match || 0} senza match (su ${d.scanned || 0} scansionati)`,
      });
      await fetchStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  /**
   * Batch reconcile: try to re-match every orphan eBay order item to a local
   * article and decrement stock for the ones that finally have a SKU match.
   * Reports how many items were realigned vs still unmatched (e.g. no SKU).
   */
  const realignOrphans = async (dryRun = false) => {
    setTriggering(dryRun ? "realign-dry" : "realign");
    setMsg(null);
    try {
      const r = await axios.post(
        `${API}/ebay/realign-all-orphans?dry_run=${dryRun ? "true" : "false"}`,
        {},
        { headers: getAuthHeaders(), withCredentials: true, timeout: 180_000 }
      );
      const d = r.data || {};
      let text =
        `${dryRun ? "🔍 Dry-run" : "🔄 Riallineamento"} → ${d.orders_scanned || 0} ordini orfani · ` +
        `${d.realigned_items || 0} item ${dryRun ? "riallineabili" : "riallineati"} ` +
        `(-${d.stock_decremented_units || 0} unità stock) · ` +
        `${d.still_unmatched || 0} SKU non trovati · ${d.skipped_no_sku || 0} senza SKU`;
      if (d.already_realigned) {
        text += ` · ${d.already_realigned} già fatti`;
      }
      setMsg({ ok: (d.still_unmatched || 0) === 0 && (d.skipped_no_sku || 0) === 0, text });
      await fetchStatus();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(null), 15000);
    }
  };

  /**
   * One-shot maintenance: scans every article, finds images saved as
   * `data:image/...;base64,...` blobs (which eBay rejects with errorId 25002)
   * and re-uploads them to Cloudflare R2, replacing the field with a public
   * HTTPS URL. Safe to run multiple times — clean articles are skipped.
   *
   * Processes in batches of 5 to stay under Cloudflare's edge timeout, and
   * auto-loops until `pending_remaining === 0`.
   */
  const repairImages = async (dryRun = false) => {
    setTriggering(dryRun ? "repair-dry" : "repair");
    setMsg(null);
    let totalMigrated = 0;
    let totalFailed = 0;
    let totalScanned = 0;
    let lastFailedDetails = "";
    const maxLoops = 20;
    try {
      for (let loop = 0; loop < maxLoops; loop++) {
        const r = await axios.post(
          `${API}/admin/articles/migrate-data-urls?dry_run=${dryRun ? "true" : "false"}&limit=5`,
          {},
          { headers: getAuthHeaders(), withCredentials: true, timeout: 120_000 }
        );
        const d = r.data || {};
        totalScanned = d.total_scanned || totalScanned;
        totalMigrated += d.migrated || 0;
        totalFailed += d.failed || 0;
        const failedDetails = (d.results || [])
          .filter((row) => row.status === "failed")
          .slice(0, 2)
          .map((row) => `• ${row.sku}: ${(row.errors || []).join("; ")}`)
          .join("\n");
        if (failedDetails) lastFailedDetails = failedDetails;
        // Show progress while looping
        setMsg({
          ok: true,
          text: `${dryRun ? "🔍" : "🛠️"} Batch ${loop + 1} → ${d.migrated || 0} ${dryRun ? "trovati" : "migrati"} · ${d.pending_remaining || 0} rimasti…`,
        });
        if (dryRun) break; // dry-run reports everything in one shot
        if ((d.migrated || 0) === 0 || (d.pending_remaining || 0) === 0) break;
      }
      let text =
        `${dryRun ? "🔍 Dry-run" : "🛠️ Riparazione completata"} → ${totalScanned} articoli scansionati · ` +
        `${totalMigrated} ${dryRun ? "da migrare" : "migrati"} · ${totalFailed} falliti`;
      if (lastFailedDetails) text += `\n${lastFailedDetails}`;
      setMsg({ ok: totalFailed === 0, text });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setTriggering(null);
      setTimeout(() => setMsg(null), 20000);
    }
  };

  const ebay = data?.ebay || {};
  const packlink = data?.packlink || {};
  const jobs = data?.jobs || [];

  const nextEbay = jobs.find((j) => j.id === "ebay_orders_sync")?.next_run_time;
  const nextPacklink = jobs.find((j) => j.id === "packlink_auto_link")?.next_run_time;

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="sync-status-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Stato Sincronizzazioni
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchStatus}
            disabled={loading}
            className="h-7 px-2 text-gray-300 hover:text-white"
            data-testid="sync-status-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* eBay row */}
        <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700" data-testid="sync-row-ebay">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-white">eBay → Magazzino</span>
            </div>
            <HealthDot health={ebay.health} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs text-gray-300">
            <div>
              <div className="text-gray-500">Ultimo sync</div>
              <div className="font-mono text-gray-200" data-testid="ebay-last-sync">{formatRelative(ebay.last_sync_at)}</div>
            </div>
            <div>
              <div className="text-gray-500">Ordini 24h</div>
              <div className="font-mono text-emerald-300 text-base font-bold">{ebay.orders_24h ?? 0}</div>
            </div>
            <div>
              <div className="text-gray-500">Totali</div>
              <div className="font-mono text-gray-200">{ebay.orders_total ?? 0}</div>
            </div>
          </div>
          {ebay.articles_in_error > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {ebay.articles_in_error} articolo/i con errore pubblicazione
            </div>
          )}
          {ebay.orphan_orders > 0 && (
            <div className="mt-2 flex flex-col gap-2 text-xs text-red-300 bg-red-900/30 border border-red-700/40 rounded px-2 py-2">
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{ebay.orphan_orders}</strong> ordine/i eBay orfano/i (SKU non matchato → stock NON scaricato).
                  Usa <em>&ldquo;🔄 Riallinea tutto&rdquo;</em> qui sotto, oppure il bottone <em>&ldquo;Riallinea Stock&rdquo;</em> sull&apos;articolo coinvolto.
                </span>
              </div>
              <div className="flex items-center gap-2 pl-5">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={triggering === "realign-dry" || triggering === "realign"}
                  onClick={() => realignOrphans(true)}
                  className="h-6 text-[11px] px-2 border-gray-500 text-gray-200 hover:bg-gray-800"
                  data-testid="realign-orphans-dry-btn"
                >
                  {triggering === "realign-dry" ? "Analizzo..." : "🔍 Dry-run"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={triggering === "realign" || triggering === "realign-dry"}
                  onClick={() => {
                    if (!window.confirm(`Riallineare ${ebay.orphan_orders} ordine/i orfano/i? Lo stock locale verrà decrementato per gli SKU che ora hanno corrispondenza, e la nuova quantità verrà spinta su eBay.`)) return;
                    realignOrphans(false);
                  }}
                  className="h-6 text-[11px] px-2 border-red-500 text-red-200 hover:bg-red-900/50"
                  data-testid="realign-orphans-btn"
                >
                  {triggering === "realign" ? "Riallineo..." : "🔄 Riallinea tutto"}
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700/50">
            <span className="text-[11px] text-gray-500">
              Prossimo: <span className="font-mono text-gray-400">{nextEbay ? formatRelative(nextEbay) : "—"}</span>
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={triggering === "ebay"}
              onClick={triggerEbay}
              className="h-6 text-[11px] px-2 border-blue-700 text-blue-300 hover:bg-blue-900/40"
              data-testid="sync-trigger-ebay-btn"
            >
              {triggering === "ebay" ? "Sync..." : "Sync ora"}
            </Button>
          </div>
        </div>

        {/* Packlink row */}
        <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700" data-testid="sync-row-packlink">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">Packlink → Ordini</span>
            </div>
            <HealthDot health={packlink.health} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs text-gray-300">
            <div>
              <div className="text-gray-500">Ultimo sync</div>
              <div className="font-mono text-gray-200" data-testid="packlink-last-sync">{formatRelative(packlink.last_sync_at)}</div>
            </div>
            <div>
              <div className="text-gray-500">Agganciati</div>
              <div className="font-mono text-emerald-300 text-base font-bold">{packlink.auto_linked_total ?? 0}</div>
            </div>
            <div>
              <div className="text-gray-500">In transito</div>
              <div className="font-mono text-gray-200">{packlink.pending_tracking ?? 0}</div>
            </div>
          </div>
          {packlink.last_errors > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {packlink.last_errors} errori nell'ultimo sync
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700/50">
            <span className="text-[11px] text-gray-500">
              Prossimo: <span className="font-mono text-gray-400">{nextPacklink ? formatRelative(nextPacklink) : "—"}</span>
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={triggering === "packlink"}
              onClick={triggerPacklink}
              className="h-6 text-[11px] px-2 border-orange-700 text-orange-300 hover:bg-orange-900/40"
              data-testid="sync-trigger-packlink-btn"
            >
              {triggering === "packlink" ? "Sync..." : "Sync ora"}
            </Button>
          </div>
        </div>

        {/* Manutenzione — Ripara immagini articoli (data:url → R2) */}
        <div className="bg-gray-900/60 rounded-lg p-3 border border-amber-700/40" data-testid="sync-row-repair">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-white">Ripara immagini articoli</span>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 leading-relaxed mb-2">
            Trova gli articoli con immagini salvate come <code className="text-amber-300">data:image/...</code> nel DB
            (che eBay rifiuta con errore 25002) e le carica su R2 con URL pubbliche valide.
            Sicuro: idempotente, gli articoli già OK vengono saltati.
          </p>
          <div className="flex items-center gap-2 pt-2 border-t border-gray-700/50">
            <Button
              size="sm"
              variant="outline"
              disabled={triggering === "repair-dry" || triggering === "repair"}
              onClick={() => repairImages(true)}
              className="h-7 text-[11px] px-2 border-gray-600 text-gray-300 hover:bg-gray-800"
              data-testid="repair-images-dry-run-btn"
            >
              {triggering === "repair-dry" ? "Analizzo..." : "🔍 Dry-run"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={triggering === "repair" || triggering === "repair-dry"}
              onClick={() => {
                if (!window.confirm("Avvio riparazione immagini? Tutti gli articoli con data:url verranno caricati su R2. Operazione sicura ma può richiedere ~30s con 18+ articoli.")) return;
                repairImages(false);
              }}
              className="h-7 text-[11px] px-2 border-amber-600 text-amber-300 hover:bg-amber-900/40"
              data-testid="repair-images-btn"
            >
              {triggering === "repair" ? "Riparo..." : "🛠️ Ripara ora"}
            </Button>
          </div>
        </div>

        {msg && (
          <div
            className={`rounded p-2 text-xs flex items-start gap-2 ${
              msg.ok
                ? "bg-emerald-900/30 border border-emerald-700 text-emerald-200"
                : "bg-red-900/30 border border-red-700 text-red-200"
            }`}
            data-testid="sync-status-toast"
          >
            {msg.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
            <span className="whitespace-pre-line">{msg.text}</span>
          </div>
        )}

        <div className="text-[10px] text-gray-500 text-center pt-1">
          Auto-refresh ogni 60s · eBay poll ogni 5 min · Packlink ogni 2 ore
        </div>
      </CardContent>
    </Card>
  );
};
